import { useEffect, useState, ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import PublicHeader from '../components/PublicHeader';
import './WeeklySummary.css';
import '../pages/Landing.css';

/* ====================================================================
   Types
   ==================================================================== */

type GoalStatus = 'on_track' | 'needs_attention' | 'great_progress';
type CategoryKey = 'diet' | 'exercise' | 'mind';

interface WeeklyCategory {
  key: CategoryKey;
  label: string;
  summary: string;
  wins: string[];
  improvements: string[];
  trends: string[];
  no_data_note: string | null;
}

interface WeeklyGoal {
  id: string;
  title: string;
  category: string;
  note: string;
  status: GoalStatus;
}

interface WeekAhead {
  suggestions: string[];
  encouragement: string;
}

interface WeeklySummaryData {
  overview: string;
  categories: WeeklyCategory[];
  goals: WeeklyGoal[];
  week_ahead: WeekAhead;
}

interface WeeklySummary {
  nanoid: string;
  period_start: string;
  period_end: string;
  generated_at: string;
  first_name: string | null;
  summary_data: WeeklySummaryData;
}

/* ====================================================================
   Helpers
   ==================================================================== */

function formatDateRange(startStr: string, endStr: string): string {
  const [sy, sm, sd] = startStr.split('-').map(Number);
  const [ey, em, ed] = endStr.split('-').map(Number);
  const startFmt = new Date(sy, sm - 1, sd).toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
  const endFmt   = new Date(ey, em - 1, ed).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  return `${startFmt} – ${endFmt}`;
}

const CATEGORY_ICON: Record<CategoryKey, string> = {
  diet:     '🥗',
  exercise: '💪',
  mind:     '🧘',
};

const STATUS_LABEL: Record<GoalStatus, string> = {
  on_track:        'On track',
  needs_attention: 'Needs attention',
  great_progress:  'Great progress',
};

/* ====================================================================
   Shell
   ==================================================================== */

function WeeklyShell({ children }: { children: ReactNode }) {
  return (
    <div className="weekly-page">
      <PublicHeader />
      {children}
      <footer className="landing-footer">
        <div className="landing-container footer-inner">
          <div className="footer-brand">
            <img src="/mosaic-icon.png" alt="mosaime" className="logo-icon-img footer-logo-icon-img" />
            <span>mosaime</span>
          </div>
          <div className="footer-links">
            <Link to="/how-it-works">How It Works</Link>
            <Link to="/blog">Blog</Link>
            <Link to="/login">Log in</Link>
            <Link to="/signup">Sign up</Link>
          </div>
          <p className="footer-copy">&copy; {new Date().getFullYear()} MOSAIME LLC. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

/* ====================================================================
   Page
   ==================================================================== */

export function WeeklySummaryPage() {
  const { nanoid } = useParams<{ nanoid: string }>();

  const [summary, setSummary] = useState<WeeklySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<'not_found' | 'expired' | 'error' | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [nanoid]);

  useEffect(() => {
    if (!nanoid) {
      setError('not_found');
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const load = async () => {
      try {
        const res = await fetch(`/api/weekly-summaries/${nanoid}`);
        if (cancelled) return;
        if (res.status === 404) { setError('not_found'); setLoading(false); return; }
        if (res.status === 410) { setError('expired');   setLoading(false); return; }
        if (!res.ok)            { setError('error');     setLoading(false); return; }
        const json = await res.json();
        setSummary(json.data);
        setLoading(false);
      } catch {
        if (!cancelled) { setError('error'); setLoading(false); }
      }
    };

    load();
    return () => { cancelled = true; };
  }, [nanoid]);

  if (loading) {
    return (
      <WeeklyShell>
        <div className="weekly-loading">
          <div className="weekly-loading-spinner" />
          <p>Loading your weekly summary…</p>
        </div>
      </WeeklyShell>
    );
  }

  if (error || !summary) {
    return (
      <WeeklyShell>
        <div className="weekly-not-found">
          <div className="weekly-not-found-icon">🌿</div>
          <h2>Summary not available</h2>
          <p>
            {error === 'expired'
              ? 'This link has expired. Text Mosaime to get your latest weekly summary.'
              : error === 'not_found'
              ? 'This weekly summary is no longer available. Open Mosaime to see your latest insights.'
              : "We couldn't load this summary. Please try again."}
          </p>
          <Link to="/" className="btn hero-cta">Go to Mosaime</Link>
        </div>
      </WeeklyShell>
    );
  }

  const { period_start, period_end, first_name, summary_data } = summary;
  const dateLabel = formatDateRange(period_start, period_end);

  return (
    <WeeklyShell>

      {/* Hero */}
      <header className="weekly-hero">
        <div className="landing-container weekly-hero-inner">
          <p className="weekly-period-label">Weekly Summary</p>
          <h1 className="weekly-title">Your Week in Review</h1>
          <p className="weekly-date">{dateLabel}</p>
          <p className="weekly-greeting">
            {first_name ? `Hi ${first_name}, here's how your week looked.` : "Hi! Here's how your week looked."}
          </p>
        </div>
      </header>

      <div className="landing-container weekly-body">

        {/* Overview */}
        <section className="weekly-overview">
          <p className="weekly-overview-text">{summary_data.overview}</p>
        </section>

        {/* Category breakdown */}
        {summary_data.categories.length > 0 && (
          <section className="weekly-section">
            <h2 className="weekly-section-title">Last Week's Breakdown</h2>
            <div className="weekly-category-list">
              {summary_data.categories.map((cat) => (
                <div key={cat.key} className="weekly-category-card">

                  {/* Card header */}
                  <div className="weekly-category-header">
                    <span className="weekly-category-icon" aria-hidden="true">
                      {CATEGORY_ICON[cat.key] ?? '✨'}
                    </span>
                    <span className="weekly-category-label">{cat.label}</span>
                  </div>

                  {/* No data state */}
                  {cat.no_data_note && cat.wins.length === 0 && cat.improvements.length === 0 ? (
                    <p className="weekly-no-data-note">{cat.no_data_note}</p>
                  ) : (
                    <>
                      {/* Summary */}
                      <p className="weekly-category-summary">{cat.summary}</p>

                      {/* Wins */}
                      {cat.wins.length > 0 && (
                        <div className="weekly-subsection">
                          <p className="weekly-subsection-label weekly-subsection-label--wins">Wins</p>
                          <ul className="weekly-item-list">
                            {cat.wins.map((w, i) => (
                              <li key={i} className="weekly-item weekly-item--win">
                                <span className="weekly-item-bullet weekly-item-bullet--win" aria-hidden="true">✓</span>
                                <span>{w}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Improvements */}
                      {cat.improvements.length > 0 && (
                        <div className="weekly-subsection">
                          <p className="weekly-subsection-label weekly-subsection-label--improvements">Areas to Work On</p>
                          <ul className="weekly-item-list">
                            {cat.improvements.map((imp, i) => (
                              <li key={i} className="weekly-item weekly-item--improvement">
                                <span className="weekly-item-bullet weekly-item-bullet--improvement" aria-hidden="true">→</span>
                                <span>{imp}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Trends */}
                      {cat.trends.length > 0 && (
                        <div className="weekly-subsection">
                          <p className="weekly-subsection-label weekly-subsection-label--trends">Trends</p>
                          <ul className="weekly-item-list">
                            {cat.trends.map((t, i) => (
                              <li key={i} className="weekly-item weekly-item--trend">
                                <span className="weekly-item-bullet weekly-item-bullet--trend" aria-hidden="true">~</span>
                                <span>{t}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* No data note as footer when there's partial data */}
                      {cat.no_data_note && (
                        <p className="weekly-no-data-note weekly-no-data-note--footer">{cat.no_data_note}</p>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Goals */}
        {summary_data.goals.length > 0 && (
          <section className="weekly-section">
            <h2 className="weekly-section-title">
              <span aria-hidden="true">🎯</span> Goals
            </h2>
            <div className="weekly-goal-list">
              {summary_data.goals.map((goal) => (
                <div key={goal.id} className="weekly-goal-item">
                  <div className="weekly-goal-header">
                    <span className={`weekly-goal-dot weekly-goal-dot--${goal.status}`} />
                    <span className="weekly-goal-title">{goal.title}</span>
                    <span className={`weekly-goal-badge weekly-goal-badge--${goal.status}`}>
                      {STATUS_LABEL[goal.status]}
                    </span>
                  </div>
                  {goal.note && <p className="weekly-goal-note">{goal.note}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Week Ahead */}
        {summary_data.week_ahead && (
          <section className="weekly-ahead">
            <h2 className="weekly-section-title">
              <span aria-hidden="true">🌱</span> This Week
            </h2>
            {summary_data.week_ahead.suggestions.length > 0 && (
              <ul className="weekly-suggestions">
                {summary_data.week_ahead.suggestions.map((s, i) => (
                  <li key={i} className="weekly-suggestion-item">
                    <span className="weekly-suggestion-bullet" aria-hidden="true">→</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            )}
            {summary_data.week_ahead.encouragement && (
              <p className="weekly-encouragement">{summary_data.week_ahead.encouragement}</p>
            )}
          </section>
        )}

        {/* CTA */}
        <div className="weekly-cta-block">
          <p className="weekly-cta-text">Questions or want to plan something specific?</p>
          <p className="weekly-cta-sub">Reply to your Mosaime SMS message to keep the conversation going.</p>
        </div>

      </div>
    </WeeklyShell>
  );
}

export default WeeklySummaryPage;
